import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import isEqualWith from 'lodash.isequalwith';
import { DragSource, DropTarget } from '../drag-and-drop';
import Render from '../render';
import TreeViewList from './tree-view-list';
import TreeViewItemPreview from './tree-view-item-preview';
import itemTypes from './constants/item-types';
import getDropPosition from './helpers/get-drop-position';
import dropPositions from './constants/drop-positions';
import { ListItem } from './constants/types';
import isNestedTo from './helpers/is-nseted-to';
import { NESTABLE, SORTABLE, NONE } from './constants/interaction-types';
import connectWhen from './helpers/connect-when';
import getLevelOfNesting from './helpers/get-level-of-nesting';
import getOriginalItem from './helpers/get-original-item';
import Tooltip from '../tooltip';

const draggingClassName = 'ws-tree-view--is-dragging';

function functionComparator(objValue, othValue) {
  if (typeof objValue === 'function') {
    return objValue.toString() === othValue.toString();
  }
  return undefined;
}

const listItemSource = {
  beginDrag(props) {
    const { onStartItemDrag, item } = props;

    /*
     * Since custom drag layer has property `pointer-events: none;`
     * we can't show dragging cursor above drag layer. As n workaround is set
     * `dragging` cursor for whole body.
     * */
    document.body.classList.add(draggingClassName);

    onStartItemDrag(item);

    return props;
  },

  isDragging(props, monitor) {
    const { id } = props;

    return id === monitor.getItem().id;
  },

  endDrag(props, monitor) {
    const dropResult = monitor.getDropResult();
    const { onEndItemDrag, item } = props;

    if (dropResult) {
      const { draggingItem, dropTarget, dropPosition } = dropResult;

      props.move(draggingItem, dropTarget, dropPosition);
    }

    onEndItemDrag(item);

    document.body.classList.remove(draggingClassName);
  },
};

const listItemTarget = {
  canDrop(props, monitor) {
    if (!monitor.isOver({ shallow: true })) {
      return false;
    }

    const { item: dropTarget } = props;
    const draggingItem = monitor.getItem();

    if (dropTarget.id === draggingItem.item.id) {
      return false;
    }

    switch (props.interaction) {
      case NESTABLE: {
        const { nestingLevel, maxNestingLevel } = props;

        return (
          !isNestedTo(draggingItem.item, dropTarget) &&
          getLevelOfNesting(draggingItem.item.items, nestingLevel) <= maxNestingLevel
        );
      }

      case SORTABLE:
        return isEqualWith(draggingItem.source, props.source, functionComparator);
      default:
        return false;
    }
  },

  drop(props, monitor, component) {
    if (!monitor.isOver({ shallow: true })) {
      return undefined;
    }

    const draggingItem = monitor.getItem();
    const dropTarget = props;
    const dropPosition = getDropPosition(props, monitor, component.getDecoratedComponentInstance());

    return {
      draggingItem,
      dropTarget,
      dropPosition,
    };
  },

  hover(props, monitor, component) {
    if (!monitor.isOver({ shallow: true }) || !monitor.canDrop()) {
      component.setDropPosition(null);

      return;
    }

    const dropPosition = getDropPosition(props, monitor, component);

    component.setDropPosition(dropPosition);
  },
};

const collectTarget = (connect, monitor) => ({
  isOver: monitor.isOver({ shallow: true }),
  connectDropTarget: connect.dropTarget(),
});

const collectSource = (connect, monitor) => ({
  connectDragSource: connect.dragSource(),
  isDragging: monitor.isDragging(),
});

class TreeViewItem extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      dropPosition: null,
    };

    this.rootElement = React.createRef();

    this.onClick = this.onClick.bind(this);
  }

  onClick() {
    const { onItemClick, item } = this.props;

    onItemClick(getOriginalItem(item));
  }

  setDropPosition(newDropPosition) {
    const { dropPosition } = this.state;

    if (newDropPosition !== dropPosition) {
      this.setState({
        dropPosition: newDropPosition,
      });
    }
  }

  render() {
    const { dropPosition } = this.state;
    const {
      item,
      connectDropTarget,
      connectDragSource,
      isDragging,
      isOver,
      move,
      find,
      nestingLevel,
      maxNestingLevel,
      interaction,
      onItemClick,
      isLast,
      onStartItemDrag,
      onEndItemDrag,
    } = this.props;

    const isDnDEnabled = interaction !== NONE;
    const hasSubtree = item.items.length > 0;
    const dropTargetClass = classNames('ws-tree-view-item__wrapper', {
      [`ws-tree-view-item__wrapper--drop-${dropPosition}`]: isOver && !isDragging && dropPosition,
    });
    const dropOverlayClass = classNames('ws-tree-view-item__drop-overlay', {
      'ws-tree-view-item__drop-overlay--visible':
        isOver && !isDragging && dropPosition === dropPositions.AFTER,
    });

    /*
     *  This part is necessary for handling case  when user moves item
     *  from subtree on the same level with parent item when
     *  it's latest item: /.github/tree-view/workaround-explanation.png
     *
     *  <Render if={isLast && hasSubtree && nestingLevel > 1}>
     *    <div className="ws-tree-view-item__drop-zone" />
     *  </Render>
     * */

    return connectWhen(
      isDnDEnabled,
      connectDropTarget
    )(
      <div className="ws-tree-view-item" ref={this.rootElement}>
        <div className={dropTargetClass} onClick={this.onClick}>
          <TreeViewItemPreview
            item={item}
            isDnDEnabled={isDnDEnabled}
            connectDragSource={connectDragSource}
            isDragging={isDragging}
          />
        </div>

        <Render if={isLast && hasSubtree && nestingLevel > 1}>
          <div className="ws-tree-view-item__drop-zone" />
        </Render>

        <Render if={hasSubtree}>
          <div className="ws-tree-view-item__subtree">
            <TreeViewList
              items={item.items}
              move={move}
              find={find}
              interaction={interaction}
              nestingLevel={nestingLevel + 1}
              maxNestingLevel={maxNestingLevel}
              onItemClick={onItemClick}
              onStartItemDrag={onStartItemDrag}
              onEndItemDrag={onEndItemDrag}
            />
          </div>
        </Render>
        <Tooltip id={item.id.toString()} />
        <div className={dropOverlayClass} />
      </div>
    );
  }
}

TreeViewItem.propTypes = {
  // eslint-disable-next-line yola/react/no-unused-prop-types
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onItemClick: PropTypes.func.isRequired,
  nestingLevel: PropTypes.number.isRequired,
  maxNestingLevel: PropTypes.number.isRequired,
  interaction: PropTypes.oneOf([NONE, SORTABLE, NESTABLE]).isRequired,
  // eslint-disable-next-line yola/react/no-unused-prop-types
  source: PropTypes.arrayOf(ListItem).isRequired,
  item: ListItem.isRequired,
  move: PropTypes.func.isRequired,
  find: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  connectDragSource: PropTypes.func.isRequired,
  isDragging: PropTypes.bool.isRequired,
  isOver: PropTypes.bool.isRequired,
  isLast: PropTypes.bool.isRequired,
  onStartItemDrag: PropTypes.func.isRequired,
  onEndItemDrag: PropTypes.func.isRequired,
};

export default DropTarget(
  itemTypes.LIST_ITEM,
  listItemTarget,
  collectTarget
)(DragSource(itemTypes.LIST_ITEM, listItemSource, collectSource)(TreeViewItem));
